<template>
  <div class="fireworks-container" v-show="isActive">
    <canvas ref="canvasRef"></canvas>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  mode: { type: String, default: 'none' },
  active: Boolean
})

const canvasRef = ref(null)
const isActive = ref(false)

let ctx = null
let animationId = null
let particles = []
let canvasW = 0
let canvasH = 0
let initialized = false

class Particle {
  constructor(x, y, color) {
    this.x = x
    this.y = y
    this.color = color
    const angle = Math.random() * Math.PI * 2
    const speed = Math.random() * 8 + 3
    this.vx = Math.cos(angle) * speed
    this.vy = Math.sin(angle) * speed
    this.life = 1
    this.decay = Math.random() * 0.012 + 0.006
    this.size = Math.random() * 4 + 2
  }
  update() {
    this.x += this.vx
    this.y += this.vy
    this.vy += 0.1
    this.vx *= 0.98
    this.vy *= 0.98
    this.life -= this.decay
  }
  draw(ctx) {
    if (this.life <= 0) return
    ctx.save()
    ctx.globalAlpha = this.life
    ctx.fillStyle = this.color
    ctx.beginPath()
    ctx.arc(this.x, this.y, this.size * this.life, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }
}

const colors = [
  '#ff351f', '#ff6816', '#ff9b1c', '#ffd36a',
  '#fff3c6', '#65ed42', '#ffffff'
]

function getEffectiveMode() {
  const m = props.mode
  if (!m || m === 'none') return 'none'
  if (m === 'circle' || m === 'center') return 'center'
  if (m === 'aside') return 'aside'
  if (m === 'random') return 'random'
  // unknown value → aside
  return 'aside'
}

function getLaunchPositions() {
  const mode = getEffectiveMode()
  switch (mode) {
    case 'center':
      return [{ x: canvasW / 2, y: canvasH * 0.4 }]
    case 'aside':
      return [
        { x: canvasW * 0.12, y: canvasH * 0.4 },
        { x: canvasW * 0.88, y: canvasH * 0.4 }
      ]
    case 'random':
      return [{
        x: Math.random() * canvasW * 0.8 + canvasW * 0.1,
        y: Math.random() * canvasH * 0.45 + canvasH * 0.1
      }]
    default:
      return []
  }
}

function launchFirework() {
  const positions = getLaunchPositions()
  for (const pos of positions) {
    const color = colors[Math.floor(Math.random() * colors.length)]
    const count = 80 + Math.floor(Math.random() * 60)
    for (let i = 0; i < count; i++) {
      particles.push(new Particle(pos.x, pos.y, color))
    }
  }
}

function animate() {
  if (!ctx) return
  ctx.clearRect(0, 0, canvasW, canvasH)

  particles = particles.filter(p => p.life > 0)
  for (const p of particles) {
    p.update()
    p.draw(ctx)
  }

  if (props.active && Math.random() < 0.06) {
    launchFirework()
  }

  animationId = requestAnimationFrame(animate)
}

function resizeCanvas() {
  const canvas = canvasRef.value
  if (!canvas) return
  canvas.width = window.innerWidth
  canvas.height = window.innerHeight
  canvasW = canvas.width
  canvasH = canvas.height
  ctx = canvas.getContext('2d')
}

function ensureInit() {
  if (initialized) return
  initialized = true
  isActive.value = true
  resizeCanvas()
  window.addEventListener('resize', resizeCanvas)
  animate()
}

onMounted(() => {
  // Always init canvas (we'll just show/hide via v-show)
  // This ensures canvas is ready when mode loads asynchronously
  resizeCanvas()
  window.addEventListener('resize', resizeCanvas)
  animate()
  initialized = true

  // If mode is already set (non-none), show immediately
  if (getEffectiveMode() !== 'none') {
    isActive.value = true
  }
})

onUnmounted(() => {
  if (animationId) cancelAnimationFrame(animationId)
  window.removeEventListener('resize', resizeCanvas)
})

// React to mode changing (async load)
watch(() => props.mode, (newMode) => {
  if (newMode && newMode !== 'none') {
    isActive.value = true
  }
}, { immediate: true })

// React to active changing → burst fireworks
watch(() => props.active, (active) => {
  if (!active) return
  if (getEffectiveMode() === 'none') return
  isActive.value = true
  // Big burst immediately
  for (let i = 0; i < 8; i++) {
    setTimeout(() => launchFirework(), i * 180)
  }
})
</script>

<style scoped lang="scss">
.fireworks-container {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 999;

  canvas {
    display: block;
    width: 100%;
    height: 100%;
  }
}
</style>
